// docs/25 in the encounter HUD: the haul (what the party has opened so far) and, once the
// objectives are met, the secured-field strip — the field is the party's to explore until every
// human is ready to finish (D1–D2). Numbers come from the server rows; nothing is computed here.
// docs/40: the chip opens onto the list of what was found (Bag.tsx `HaulList`).
// docs/138 XO4: in an exploration the haul is personal — the chip shows my heroes' own take
// ("Yours": their gold from `haul_gold`, their pieces by `haul_item.opened_by`); XO1: once the
// wonder is found the strip says so and the button is Ready to leave.
import { useMemo } from 'react';
import { Badge, Button, Reveal } from '../components';
import { useEncounter } from '../../core/encounter/session';
import { useEncounterHaul, useEncounterSecured, useFinishVotes, useHaulItems } from '../../core/subscriptions/hooks';
import { identityHex } from '../../core/net/connection';
import { rpc } from '../../core/net/reducers';
import { useHaulGold } from '../../core/explore/blessing';
import { HaulList } from './Bag';

/** docs/138 XO1: the secured exploration shows its turns left only once they run short. */
export const EXPLORE_TURNS_SHOWN = 20;

/** True while the board is an exploration (its spoils are personal, docs/138 XO4). */
export const useIsExploration = (): boolean => useEncounter((s) => !!s.snapshot?.explore);

/** My heroes in this encounter (the hero ids I control or own). */
function useMyHeroIds(): Set<number> {
  const owners = useEncounter((s) => s.owners);
  const me = useEncounter((s) => s.me);
  return useMemo(() => new Set(Object.entries(owners).filter(([, o]) => o === me).map(([h]) => Number(h))), [owners, me]);
}

/** "Haul: 73 gold · 4 items" — empty until a stash is opened (nothing is auto-looted, D3). */
export function HaulChip() {
  const encounterId = useEncounter((s) => s.encounterId);
  const explore = useIsExploration();
  const haul = useEncounterHaul(encounterId);
  const items = useHaulItems(encounterId);
  const golds = useHaulGold(explore ? encounterId : undefined);
  const mine = useMyHeroIds();
  if (explore) {
    const myItems = items.filter((i) => mine.has(Number(i.openedBy)));
    const gold = [...golds.entries()].reduce((s, [h, g]) => s + (mine.has(h) ? g : 0), 0);
    return (
      <section className="fk-hud__haul fk-hud__haul--yours fk-hit" data-testid="haul" data-personal="1" data-gold={gold} data-items={myItems.length} aria-label="What your heroes have picked up">
        <h3 className="fk-hud__label" title="Finders keepers: what each hero picks up here is theirs alone">Yours</h3>
        <span className="fk-hud__haul-line">
          <b>{gold}</b> gold · <b>{myItems.length}</b> item{myItems.length === 1 ? '' : 's'} <Badge tone="gold" title="What each hero picks up here is theirs alone. Experience is shared.">finders keepers</Badge>
        </span>
        <HaulList personal={mine} />
      </section>
    );
  }
  const relics = items.filter((i) => i.relic).length;
  return (
    <section className="fk-hud__haul fk-hit" data-testid="haul" data-gold={haul?.gold ?? 0} data-items={items.length} aria-label="The haul so far">
      <h3 className="fk-hud__label">Haul</h3>
      <span className="fk-hud__haul-line">
        <b>{haul?.gold ?? 0}</b> gold · <b>{items.length}</b> item{items.length === 1 ? '' : 's'}{relics > 0 && <> · <Badge tone="gold">{relics} relic{relics === 1 ? '' : 's'}</Badge></>}
      </span>
      <HaulList />
    </section>
  );
}

/** True once this encounter's field is secured (the HUD hides the "this plan ends it" stamp then). */
export function useSecured(): boolean {
  return !!useEncounterSecured(useEncounter((s) => s.encounterId));
}

/** What the secured strip says, by encounter kind (docs/25 D1, docs/138 XO1). */
export function securedWords(p: { explore: boolean; outcome: number; left: number }): { word: string; text: string; left?: string; button: string } {
  const left = `${p.left} turn${p.left === 1 ? '' : 's'} left`;
  if (p.explore) {
    return { word: 'The wonder is found', text: 'Explore on, search for hidden loot, then leave when everyone is ready.', ...(p.left < EXPLORE_TURNS_SHOWN ? { left } : {}), button: 'Ready to leave' };
  }
  return { word: p.outcome === 3 ? 'They yield' : 'Objectives met', text: 'Explore, open what you find, then finish.', left, button: 'Ready to finish' };
}

/** The secured-field strip over the board: what it means, pulses left, and Ready to finish (n/m). */
export function SecuredStrip() {
  const encounterId = useEncounter((s) => s.encounterId);
  const pulse = useEncounter((s) => s.pulseIndex);
  const state = useEncounter((s) => s.state);
  const me = useEncounter((s) => s.me);
  const owners = useEncounter((s) => s.owners);
  const explore = useIsExploration();
  const sec = useEncounterSecured(encounterId);
  const votes = useFinishVotes(encounterId);
  if (!sec || !encounterId || state === 3) return null;
  const humans = [...new Set(Object.values(owners))];
  const voted = new Set(votes.map((v) => identityHex(v.owner)));
  const ready = humans.filter((h) => voted.has(h)).length;
  const mine = voted.has(me);
  const w = securedWords({ explore, outcome: sec.outcome, left: Math.max(0, sec.untilPulse - pulse) });
  return <SecuredView words={w} ready={ready} humans={humans.length} mine={mine} explore={explore}
    onReady={() => void (mine ? rpc.unfinishEncounter(encounterId) : rpc.finishEncounter(encounterId))} />;
}

/** The strip as a pure view (the HUD and the `?explore=1` gallery). */
export function SecuredView({ words: w, ready, humans, mine, explore, onReady }: { words: ReturnType<typeof securedWords>; ready: number; humans: number; mine: boolean; explore: boolean; onReady(): void }) {
  return (
    <Reveal kind="pop" className={`fk-hud__secured fk-hit ${explore ? 'fk-hud__secured--explore' : ''}`} role="status" data-testid="secured-banner" data-ready={ready} data-humans={humans}>
      <span className="fk-hud__secured-word">{w.word}</span>
      <span className="fk-hud__secured-text" title={w.text}>{w.text}</span>
      {w.left && <span className="fk-hud__secured-left" data-testid="secured-left">{w.left}</span>}
      <Button small primary={!mine} data-testid="finish-ready" aria-pressed={mine} onClick={onReady}>
        {mine ? 'Ready ✓' : w.button} · {ready}/{humans || 1}
      </Button>
    </Reveal>
  );
}
